import React, { useRef, useEffect } from 'react';

/**
 * ============================================================================
 * 文件模块：QuantumBackground.jsx
 * [说明]: 这是一个负责背景动画的独立组件。
 * ============================================================================
 */
const QuantumBackground = ({ themeMode = "dark" }) => {
  const canvasRef = useRef(null);
  const themeModeRef = useRef(themeMode);

  // 更新 themeMode 引用，确保动画函数能访问到最新值
  useEffect(() => {
    themeModeRef.current = themeMode;
    // 当主题改变时，立即清除画布并更新背景色
    if (canvasRef.current) {
      const ctx = canvasRef.current.getContext("2d");
      ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
      // 确保背景色立即更新
      const bgColor = themeMode === "light" ? "#f9fafb" : "#080808";
      canvasRef.current.style.backgroundColor = bgColor;
    }
  }, [themeMode]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    let width = window.innerWidth;
    let height = window.innerHeight;
    canvas.width = width;
    canvas.height = height;
    
    // 设置初始背景色清除
    ctx.clearRect(0, 0, width, height);

    // [用户修改]: 调整粒子数量以平衡性能和视觉效果
    let particles = [];
    const particleCount = width < 768 ? 30 : 60;
    const connectionDistance = 180;
    const mouseDistance = 250;
    let mouse = { x: null, y: null };

    // 绘制六边形（代表化学/有机）
    const drawHexagon = (ctx, x, y, radius, color) => {
      ctx.beginPath();
      for (let i = 0; i < 6; i++) {
        ctx.lineTo(
          x + radius * Math.cos((Math.PI / 3) * i),
          y + radius * Math.sin((Math.PI / 3) * i)
        );
      }
      ctx.closePath();
      ctx.strokeStyle = color;
      ctx.lineWidth = 1.5;
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(x, y, 2, 0, Math.PI * 2);
      ctx.fillStyle = color;
      ctx.fill();
    };

    class Particle {
      constructor() {
        this.x = Math.random() * width;
        this.y = Math.random() * height;
        this.vx = (Math.random() - 0.5) * 0.3;
        this.vy = (Math.random() - 0.5) * 0.3;
        this.size = Math.random() * 2 + 1;
        // 70%是化学分子，30%是量子点
        this.type = Math.random() > 0.7 ? "molecule" : "quantum";
        this.rotation = Math.random() * Math.PI;
        this.rotationSpeed = (Math.random() - 0.5) * 0.02;
        this.isCyan = Math.random() > 0.5;
        this.baseColor = this.isCyan ? "6, 182, 212" : "236, 72, 153";
      }

      update() {
        this.x += this.vx;
        this.y += this.vy;
        this.rotation += this.rotationSpeed;
        if (this.x < 0 || this.x > width) this.vx *= -1;
        if (this.y < 0 || this.y > height) this.vy *= -1;

        // 鼠标交互逻辑
        if (mouse.x != null) {
          let dx = mouse.x - this.x;
          let dy = mouse.y - this.y;
          let distance = Math.sqrt(dx * dx + dy * dy);
          if (distance < mouseDistance) {
            const force = (mouseDistance - distance) / mouseDistance;
            this.x -= (dx / distance) * force * 1.5;
            this.y -= (dy / distance) * force * 1.5;
          }
        }
      }

      draw(currentThemeMode) {
        const isLight = currentThemeMode === "light";
        const alpha = this.type === "molecule" ? (isLight ? 0.2 : 0.5) : (isLight ? 0.3 : 0.8);
        const color = `rgba(${this.baseColor}, ${alpha})`;
        if (this.type === "molecule") {
          ctx.save();
          ctx.translate(this.x, this.y);
          ctx.rotate(this.rotation);
          drawHexagon(ctx, 0, 0, this.size * 6, color);
          ctx.restore();
        } else {
          ctx.beginPath();
          ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
          ctx.fillStyle = color;
          ctx.fill();
        }
      }
    }

    function init() {
      particles = [];
      for (let i = 0; i < particleCount; i++) {
        particles.push(new Particle());
      }
    }

    let animationFrameId;
    let isAnimating = true;
    
    function animate() {
      if (!isAnimating) return;
      
      ctx.clearRect(0, 0, width, height);
      // 从 ref 中获取最新的 themeMode，而不是闭包中的旧值
      const currentThemeMode = themeModeRef.current;
      const isLight = currentThemeMode === "light";
      
      for (let i = 0; i < particles.length; i++) {
        for (let j = i; j < particles.length; j++) {
          let dx = particles[i].x - particles[j].x;
          let dy = particles[i].y - particles[j].y;
          let distance = Math.sqrt(dx * dx + dy * dy);
          if (distance < connectionDistance) {
            // 连接逻辑：模拟化学键或者神经网络连接
            const isMixed = particles[i].isCyan !== particles[j].isCyan;
            let opacity = 1 - distance / connectionDistance;
            ctx.beginPath();
            if (isLight) {
              ctx.strokeStyle = isMixed
                ? `rgba(0, 0, 0, ${opacity * 0.1})`
                : `rgba(${particles[i].baseColor}, ${opacity * 0.15})`;
            } else {
              ctx.strokeStyle = isMixed
                ? `rgba(255, 255, 255, ${opacity * 0.2})`
                : `rgba(${particles[i].baseColor}, ${opacity * 0.2})`;
            }
            ctx.lineWidth = isMixed ? 1.5 : 0.5;
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.stroke();
          }
        }
        particles[i].update();
        particles[i].draw(currentThemeMode); // 传递当前的 themeMode
      }
      animationFrameId = requestAnimationFrame(animate);
    }

    init();
    animate();

    const handleResize = () => {
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = width;
      canvas.height = height;
      init();
    };
    const handleMouseMove = (e) => {
      mouse.x = e.clientX;
      mouse.y = e.clientY;
    };

    window.addEventListener("resize", handleResize);
    window.addEventListener("mousemove", handleMouseMove);
    
    return () => {
      isAnimating = false;
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("mousemove", handleMouseMove);
    };
  }, [themeMode]);

  // 根据主题设置背景色
  const backgroundColor = themeMode === "light" 
    ? "#f9fafb" // gray-50 - 浅色背景
    : "#080808"; // 深色背景

  return (
    <canvas
      ref={canvasRef}
      className="fixed top-0 left-0 w-full h-full -z-10"
      style={{
        backgroundColor: backgroundColor,
        transition: "background-color 0.2s ease-in-out"
      }}
    />
  );
};

export default QuantumBackground;
